import { useEffect, useMemo, useState } from "react";

const uid = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return "";
}

const splitCode = (code) => String(code || "").split(".").map((x) => parseInt(x, 10) || 0);

const compareCodes = (a, b) => {
  const aa = splitCode(a);
  const bb = splitCode(b);
  const L = Math.max(aa.length, bb.length);
  for (let i = 0; i < L; i++) {
    const x = aa[i] ?? -1;
    const y = bb[i] ?? -1;
    if (x !== y) return x - y;
  }
  return 0;
};

const normalizeAccountsFromApi = (payload) => {
  const arr = payload?.accounts || payload?.data || payload || [];
  if (!Array.isArray(arr)) return [];
  return arr
    .map((a) => ({
      id: String(a.id ?? a.pk ?? uid()),
      parentId: a.parentId === null || a.parentId === undefined ? null : String(a.parentId ?? a.padre_id ?? a.padre),
      code: String(a.code ?? a.codigo ?? ""),
      name: String(a.name ?? a.nombre ?? ""),
      type: String(a.type ?? a.tipo ?? ""),
      balance: Number(a.balance ?? a.saldo ?? 0) || 0,
    }))
    .filter((a) => a.code && a.name);
};

const toNumber = (v) => {
  const s = String(v ?? "").trim().replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

export default function useAccountsController(navigate) {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [expandedIds, setExpandedIds] = useState([]);
  const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAccountId, setMenuAccountId] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("add");
  const [editorParentId, setEditorParentId] = useState(null);
  const [editorAccountId, setEditorAccountId] = useState(null);

  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("");
  const [formBalance, setFormBalance] = useState("0.00");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteAccountId, setDeleteAccountId] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);

  const closeSnackbar = () => setSnackbarOpen(false);

  const childrenByParent = useMemo(() => {
    const m = new Map();
    for (const a of accounts) {
      const key = a.parentId ?? "__root__";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((x, y) => compareCodes(x.code, y.code));
      m.set(k, arr);
    }
    return m;
  }, [accounts]);

  const hasChildren = (id) => (childrenByParent.get(String(id)) || []).length > 0;

  const allExpandableIds = useMemo(() => {
    const ids = [];
    for (const a of accounts) if (hasChildren(a.id)) ids.push(a.id);
    return ids;
  }, [accounts, childrenByParent]);

  const isAllExpanded = allExpandableIds.length > 0 && expandedIds.length === allExpandableIds.length;

  const toggleNode = (id) => {
    setExpandedIds((prev) => {
      const s = new Set(prev);
      const sid = String(id);
      if (s.has(sid)) s.delete(sid);
      else s.add(sid);
      return Array.from(s);
    });
  };

  const toggleExpandAll = () => {
    if (isAllExpanded) setExpandedIds([]);
    else setExpandedIds(allExpandableIds);
  };

  const openMenuFor = (e, accountId) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuAccountId(String(accountId));
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuAccountId(null);
  };

  const selectedAccount = useMemo(() => {
    if (!menuAccountId) return null;
    return accounts.find((a) => a.id === String(menuAccountId)) || null;
  }, [menuAccountId, accounts]);

  const canDeleteSelected = !!selectedAccount && !!selectedAccount.parentId;

  const computeNextChildCodePreview = (parentId) => {
    const parent = accounts.find((a) => a.id === String(parentId));
    if (!parent) return "";
    const kids = (childrenByParent.get(String(parentId)) || []).map((k) => {
      const parts = splitCode(k.code);
      return parts[parts.length - 1] || 0;
    });
    const max = kids.length ? Math.max(...kids) : 0;
    return `${parent.code}.${max + 1}`;
  };

  const ensureCsrfCookie = async () => {
    try {
      await fetch("/sistema/ensure-csrf/", { method: "GET", credentials: "include" });
    } catch {}
  };

  const ensureLogged = async () => {
    try {
      const r = await fetch("/sistema/get_user_info/", { method: "GET", credentials: "include" });
      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const csrf = getCSRFToken();
      const r = await fetch("/cuentas/listar/", {
        method: "GET",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
      });
      if (r.status === 401 || r.status === 403) {
        setLoading(false);
        navigate("/login");
        return;
      }
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        setAccounts(normalizeAccountsFromApi(d));
      }
    } catch {}
    setLoading(false);
  };

  const fetchTypes = async () => {
    setTypesLoading(true);
    try {
      const csrf = getCSRFToken();
      const r = await fetch("/cuentas/tipos/", {
        method: "GET",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        const arr = Array.isArray(d?.types) ? d.types : [];
        setTypes(
          arr
            .map((x) => ({
              value: String(x.value ?? ""),
              label: String(x.label ?? x.value ?? ""),
            }))
            .filter((x) => x.value)
        );
      }
    } catch {}
    setTypesLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      await ensureCsrfCookie();
      const ok = await ensureLogged();
      if (!ok) return;
      await Promise.all([fetchAccounts(), fetchTypes()]);
    };
    init();
  }, [navigate]);

  const openTypeModal = () => setTypeModalOpen(true);
  const closeTypeModal = () => setTypeModalOpen(false);

  const onSelectType = (value) => {
    setFormType(String(value || ""));
    setTypeModalOpen(false);
  };

  const openAddDialog = (parentId) => {
    const parent = accounts.find((a) => a.id === String(parentId));
    if (!parent) return;

    setEditorMode("add");
    setEditorParentId(String(parentId));
    setEditorAccountId(null);
    setFormCode(computeNextChildCodePreview(String(parentId)));
    setFormName("");
    setFormBalance("0.00");
    setFormType("");
    setEditorOpen(true);

    setExpandedIds((prev) => {
      const s = new Set(prev);
      s.add(String(parentId));
      return Array.from(s);
    });
  };

  const openEditDialog = (accountId) => {
    const a = accounts.find((x) => x.id === String(accountId));
    if (!a) return;

    setEditorMode("edit");
    setEditorParentId(a.parentId ?? null);
    setEditorAccountId(a.id);
    setFormCode(a.code || "");
    setFormName(a.name || "");
    setFormBalance(String(a.balance ?? 0));
    setFormType(String(a.type || ""));
    setEditorOpen(true);
  };

  const openDeleteDialog = (accountId) => {
    setDeleteAccountId(String(accountId));
    setDeleteOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorMode("add");
    setEditorParentId(null);
    setEditorAccountId(null);
    setFormCode("");
    setFormName("");
    setFormType("");
    setFormBalance("0.00");
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteAccountId(null);
  };

  const toast = (type, message) => {
    setSnackbarType(type);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const balanceDisabled = useMemo(() => {
    if (!editorOpen) return false;
    if (editorMode !== "edit") return false;
    if (!editorAccountId) return false;
    return hasChildren(editorAccountId);
  }, [editorOpen, editorMode, editorAccountId, childrenByParent]);

  const typeDisabled = useMemo(() => {
    if (!editorOpen) return false;
    if (editorMode !== "edit") return false;
    if (!editorAccountId) return false;
    return hasChildren(editorAccountId);
  }, [editorOpen, editorMode, editorAccountId, childrenByParent]);

  const isEditorTargetParent = useMemo(() => {
    if (!editorOpen || editorMode !== "edit" || !editorAccountId) return false;
    return hasChildren(editorAccountId);
  }, [editorOpen, editorMode, editorAccountId, childrenByParent]);

  const saveEditor = async () => {
    const name = String(formName || "").trim();
    if (!name) return;

    const csrf = getCSRFToken();

    if (editorMode === "add") {
      const tipo = String(formType || "").trim();
      if (!tipo) {
        toast("error", "Debes seleccionar un tipo");
        return;
      }
      try {
        const r = await fetch("/cuentas/crear/", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRFToken": csrf } : {}),
          },
          body: JSON.stringify({
            padre_id: editorParentId,
            nombre: name,
            saldo: toNumber(formBalance),
            tipo,
          }),
        });

        if (r.ok) {
          toast("success", "Cuenta creada correctamente");
          closeEditor();
          await fetchAccounts();
          return;
        }

        const d = await r.json().catch(() => ({}));
        toast("error", d?.error || "No se pudo crear la cuenta");
      } catch {
        toast("error", "No se pudo crear la cuenta");
      }
      return;
    }

    try {
      const payload = { nombre: name };

      if (!typeDisabled && !isEditorTargetParent) {
        const tipo = String(formType || "").trim();
        if (!tipo) {
          toast("error", "Debes seleccionar un tipo");
          return;
        }
        payload.tipo = tipo;
      }

      if (!balanceDisabled) payload.saldo = toNumber(formBalance);

      const r = await fetch(`/cuentas/editar/${editorAccountId}/`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (r.ok) {
        toast("success", "Cuenta modificada correctamente");
        closeEditor();
        await fetchAccounts();
        return;
      }

      const d = await r.json().catch(() => ({}));
      toast("error", d?.error || "No se pudo modificar la cuenta");
    } catch {
      toast("error", "No se pudo modificar la cuenta");
    }
  };

  const confirmDelete = async () => {
    if (!deleteAccountId) return;
    const target = accounts.find((a) => a.id === String(deleteAccountId));
    if (!target || !target.parentId) {
      closeDelete();
      return;
    }

    const csrf = getCSRFToken();
    try {
      const r = await fetch(`/cuentas/eliminar/${deleteAccountId}/`, {
        method: "DELETE",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
      });

      if (r.ok) {
        toast("success", "Cuenta eliminada correctamente");
        closeDelete();
        await fetchAccounts();
        return;
      }

      const d = await r.json().catch(() => ({}));
      toast("error", d?.error || "No se pudo eliminar la cuenta");
    } catch {
      toast("error", "No se pudo eliminar la cuenta");
    }
  };

  const displayBalanceById = useMemo(() => {
    const byId = new Map();
    const byParent = childrenByParent;

    const ownBalance = (id) => {
      const a = accounts.find((x) => x.id === String(id));
      return Number(a?.balance || 0) || 0;
    };

    const calc = (id) => {
      const kids = byParent.get(String(id)) || [];
      if (!kids.length) {
        const b = ownBalance(id);
        byId.set(String(id), b);
        return b;
      }
      let total = 0;
      for (const k of kids) total += calc(k.id);
      byId.set(String(id), total);
      return total;
    };

    const roots = byParent.get("__root__") || [];
    for (const r of roots) calc(r.id);

    return byId;
  }, [accounts, childrenByParent]);

  return {
    open,
    setOpen,
    submenuOpen,
    setSubmenuOpen,

    accounts,
    loading,
    expandedSet,
    isAllExpanded,
    childrenByParent,
    hasChildren,
    toggleNode,
    toggleExpandAll,

    menuAnchorEl,
    openMenuFor,
    closeMenu,
    selectedAccount,
    canDeleteSelected,

    openAddDialog,
    openEditDialog,
    openDeleteDialog,

    editorOpen,
    editorMode,
    formCode,
    formName,
    setFormName,
    formType,
    setFormType,
    formBalance,
    setFormBalance,
    balanceDisabled,
    typeDisabled,
    isEditorTargetParent,
    closeEditor,
    saveEditor,

    deleteOpen,
    closeDelete,
    confirmDelete,

    snackbarOpen,
    snackbarType,
    snackbarMessage,
    closeSnackbar,

    displayBalanceById,

    types,
    typesLoading,
    typeModalOpen,
    openTypeModal,
    closeTypeModal,
    onSelectType,
  };
}
